import {Button, Icon} from '@wandb/components';
import React, {useState} from 'react';

import {WBAgentNetworkDomainList} from './WBAgentNetworkDomainList';

export interface WBAgentManageNetworkAccessProps {
  /** Currently-active network domains. */
  domains: string[];
  /** Apply the new allowlist (the still-checked domains) in one turn. */
  onSubmit: (keptDomains: string[]) => void;
  /** Close the manager without changes. */
  onCancel: () => void;
  /** Whether the controls are disabled (e.g. a run is in progress). */
  isDisabled?: boolean;
}

/**
 * WBAgentManageNetworkAccess lets the user review the active network allowlist
 * and revoke domains in bulk, from a popover on the composer's network button.
 * All active domains start checked; unchecking marks them for revocation;
 * submitting applies the remaining set as the next turn's allowlist in a single
 * turn.
 */
export const WBAgentManageNetworkAccess = ({
  domains,
  onSubmit,
  onCancel,
  isDisabled = false,
}: WBAgentManageNetworkAccessProps) => {
  // All active domains start checked; unchecking marks one for revocation.
  const [selected, setSelected] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(domains.map(d => [d, true]))
  );

  const toggle = (domain: string) =>
    setSelected(prev => ({...prev, [domain]: !prev[domain]}));

  const kept = domains.filter(d => selected[d]);
  const changed = kept.length !== domains.length;

  return (
    <div className="flex flex-col gap-12">
      <div className="flex items-center gap-8">
        <span className="inline-flex shrink-0">
          <Icon icon="lock-open" size="sm" color="sienna" />
        </span>
        <span className="text-sm font-semibold text-moon-800">
          Manage network access
        </span>
      </div>

      <span className="text-sm text-moon-700">
        Uncheck domains to revoke them. Changes apply on your next turn.
      </span>

      <WBAgentNetworkDomainList
        domains={domains}
        selected={selected}
        onToggle={toggle}
      />

      <div className="flex justify-end gap-8">
        <Button variant="secondary" isDisabled={isDisabled} onPress={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={isDisabled || !changed}
          onPress={() => onSubmit(kept)}>
          Update access
        </Button>
      </div>
    </div>
  );
};
